import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { TokenStorageService } from './token-storage.service';
import { AuthService } from '../services/auth.service';

/** Semáforo que indica si hay una petición de refresco de token en curso. */
let isRefreshing = false;

/** * Sujeto reactivo que actúa como "sala de espera".
 * Emite `null` mientras se refresca el token, y emite el nuevo token cuando se obtiene.
 */
const refreshTokenSubject = new BehaviorSubject<string | null>(null);
/**
 * Interceptor HTTP funcional para gestionar la autenticación y el refresco de tokens JWT evitando colisiones.
 *
 * Intercepta las peticiones salientes para inyectar el token de acceso. Si el backend devuelve un 401,
 * verifica si ya hay un proceso de refresco activo. Si no lo hay, solicita un nuevo token. Si ya hay
 * uno activo, encola las peticiones subsiguientes utilizando un `BehaviorSubject` hasta que el nuevo
 * token esté disponible, evitando invalidar la sesión por múltiples intentos de refresco simultáneos.
 *
 * @param req La petición HTTP original en curso.
 * @param next El manejador que delega la petición al siguiente interceptor o al backend.
 * @returns Un `Observable` que emite el evento HTTP resultante o propaga el error.
 * @throws Propaga el `HttpErrorResponse` si el error no es 401 o si el refresco falla.
 *
 * @example
 * // En app.config.ts:
 * provideHttpClient(withInterceptors([authInterceptor]))
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenStorage = inject(TokenStorageService);
  const authService = inject(AuthService);
  const token = tokenStorage.getToken();

  let authReq = req;

  if (token) {
    authReq = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        error.status === 401 &&
        !authReq.url.includes('login') &&
        !authReq.url.includes('refresh-token')
      ) {
        // 1. Si NO estamos refrescando el token actualmente
        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null); // Bloqueamos la sala de espera

          return authService.refreshToken().pipe(
            switchMap((newToken) => {
              isRefreshing = false;
              refreshTokenSubject.next(newToken); // Avisamos a las peticiones en espera

              const retryReq = authReq.clone({
                setHeaders: { Authorization: `Bearer ${newToken}` },
              });
              return next(retryReq);
            }),
            catchError((refreshError) => {
              isRefreshing = false;
              authService.logout();
              return throwError(() => refreshError);
            }),
          );
        } else {
          // 2. Si YA ESTAMOS refrescando, ponemos esta petición en "espera"
          return refreshTokenSubject.pipe(
            filter((newToken) => newToken !== null), // Esperamos hasta que el token NO sea null
            take(1), // Tomamos el primer token válido que llegue y nos desuscribimos
            switchMap((newToken) => {
              const retryReq = authReq.clone({
                setHeaders: { Authorization: `Bearer ${newToken}` },
              });
              return next(retryReq); // Reintentamos la petición con el token fresco
            }),
          );
        }
      }

      // Si no es un 401 o es un endpoint excluido, devolvemos el error tal cual
      return throwError(() => error);
    }),
  );
};
